import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { GridFooterContainer } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import React from "react";
import { useSearchParams } from "react-router-dom";

export const calculateMinWidth = (columnField, rows = [], formatters = {}) => {
  const headerWidth = calculateTextWidth(columnField.headerName) + 24; // Add some padding
  let maxContentWidth = 0;

  rows.forEach((row) => {
    let cellContent = row[columnField.field];

    // Apply formatter if exists
    if (formatters[columnField.field]) {
      cellContent = formatters[columnField.field](cellContent, row);
    }

    if (cellContent) {
      const contentWidth = calculateTextWidth(cellContent.toString());
      maxContentWidth = Math.max(maxContentWidth, contentWidth);
    }
  });
  return Math.max(headerWidth, maxContentWidth) + 32; // Add some padding
};

const calculateTextWidth = (text, font = "13px Proxima Nova") => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font;
  return context.measureText(text).width;
};

export const CustomLoadingOverlay = ({ message }) => {
  return (
    <Stack
      direction="row"
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={1}
    >
      <CircularProgress
        sx={{
          color: "#000",
        }}
      />
    </Stack>
  );
};

const DataTable = ({
  ownPaginationHandler = false,
  paginationModel = {},
  handleRowClick = () => {},
  columnVisibilityModel = {},
  setColumnVisibilityModel = () => {},
  setPaginationModel,
  driverIdBool = false,
  alternativeId = "",
  totalCount = 0,
  columns = [],
  row = [],
  loading = false,
  paginationInnerObject = false,
  ...rest
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const handlePaginationModel = (
    value,
    setPaginationModel,
    paginationInnerObject = false
  ) => {
    // Update the search parameters
    searchParams.set("page", value?.page || 0);
    searchParams.set("limit", value?.pageSize || 10);
    // Set the new search parameters with the replace option
    setSearchParams(searchParams, { replace: true });
    if (paginationInnerObject) {
      setPaginationModel((prev) => ({ ...prev, paginationModel: value }));
    } else {
      setPaginationModel((prev) => ({ ...prev, ...value }));
    }
  };
  return (
    <Box
      height="616px"
      sx={{
        overflowX: "auto",
        overflowY: "hidden",
        maxWidth: "calc(100vw - 200px - 3rem)",
        "& .MuiDataGrid-root": {
          // border: "1px solid black",
          // boxShadow: "0px 3px 22px 0px #2E2D740D",
          borderRadius: "8px",
          boxShadow: "0px 3px 22px 0px #2E2D740D",
        },
        "& .MuiDataGrid-cell": {
          // borderBottom: "1px solid black",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "#E4E4F0",
          color: "#1D1F2C",
          borderBottom: "none",
        },

        "& .MuiDataGrid-row:nth-of-type(even)": {
          backgroundColor: "#F9F9FC",
        },
        "& .MuiDataGrid-row:nth-of-type(odd)": {
          backgroundColor: "#ffffff",
        },

        "& .MuiDataGrid-columnHeaderTitle": {
          fontFamily: "Proxima Nova",
          fontWeight: 600,
          fontSize: "12px",
          lineHeight: "12px",
        },

        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: "white",
          "&::-webkit-scrollbar": {
            width: "10px",
            height: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#000",
            borderRadius: "5px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#333",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#000 #E0E2E76",
        },

        "& .MuiDataGrid-footerContainer": {
          backgroundColor: "white",
          borderTop: "1px solid #E0E2E766",
          height: "36px",
          minHeight: "36px",
          borderBottomLeftRadius: "8px",
          borderBottomRightRadius: "8px",
        },
        "& .MuiDataGrid-toolbarContainer  .MuiButton-text": {
          color: `black !important`,
        },
        "& .MuiDataGrid-sortIcon": {
          color: "black",
          backgroundColor: "white",
        },
      }}
    >
      <DataGrid
        getRowId={(row) => {
          return alternativeId ? row?.[alternativeId] : row.id;
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
        rows={row || []}
        columns={columns.map((col) => ({
          ...col,
          minWidth: col.minWidth ?? calculateMinWidth(col, row, col.formatter),
          // Set a minimum width for each column
        }))}
        sx={{ cursor: "pointer" }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: paginationModel?.pageSize ?? 0,
              page: paginationModel?.page ?? 0,
            },
          },
        }}
        paginationMode="server"
        loading={loading}
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        onPaginationModelChange={
          ownPaginationHandler === false
            ? (value) => {
                handlePaginationModel(
                  value,
                  setPaginationModel,
                  paginationInnerObject
                );
              }
            : (value) => setPaginationModel(value)
        }
        pageSizeOptions={[15, 30, 50]}
        rowCount={Math.ceil(totalCount)}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        onRowClick={handleRowClick}
        rowHeight={36}
        columnHeaderHeight={36}
        {...rest}
      />
    </Box>
  );
};

export default DataTable;
